import axios from 'axios';

/**
 * Base service for handling insurance API interactions
 * This provides standardized methods for all insurance-related services
 */
export function useBaseInsuranceService() {
  // Create an axios instance with common configuration
  const apiClient = axios.create({
    baseURL: '/api',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  });
  
  /**
   * Standard error handler for API requests
   */
  const handleApiError = (error) => {
    console.error('API Error:', error);
    
    // Format error message for display
    let errorMessage = 'An unexpected error occurred. Please try again.';
    
    if (error.response) {
      // Log detailed response information for debugging
      console.error('API Error Response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        url: error.response.config?.url,
        method: error.response.config?.method,
        data: error.response.data
      });
      
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.status === 422) {
        // Validation error with detailed field errors
        errorMessage = 'Please check your form for errors.';
        
        // Log specific validation errors
        if (error.response.data && error.response.data.errors) {
          console.error('Validation errors:', error.response.data.errors);
        }
      } else if (error.response.status === 401) {
        errorMessage = 'You must be logged in to perform this action.';
      } else if (error.response.status === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      } else if (error.response.status >= 500) {
        errorMessage = 'A server error occurred. Please try again later.';
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
      errorMessage = 'No response from server. Please check your connection.';
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Request setup error:', error.message);
    }
    
    // Return enhanced error object with original error
    throw {
      message: errorMessage,
      originalError: error,
      validationErrors: error.response?.data?.errors || {}
    };
  };
  
  /**
   * Submit form data to the specified endpoint
   * Handles both JSON and FormData submissions
   * 
   * @param {string} endpoint - API endpoint to call
   * @param {object|FormData} formData - Data to submit
   * @param {boolean} isMultipart - Whether the data should be submitted as multipart/form-data
   * @returns {Promise} - Promise that resolves to the response data
   */
  const submitForm = async (endpoint, formData, isMultipart = false) => {
    try {
      const headers = isMultipart ? 
        { 'Content-Type': 'multipart/form-data' } : 
        { 'Content-Type': 'application/json' };
      
      const response = await apiClient.post(endpoint, formData, { headers });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  };
  
  /**
   * Fetch data from the specified endpoint
   * 
   * @param {string} endpoint - API endpoint to call
   * @param {object} params - Query parameters
   * @returns {Promise} - Promise that resolves to the response data
   */
  const fetchData = async (endpoint, params = {}) => {
    try {
      const response = await apiClient.get(endpoint, { params });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  };
  
  /**
   * Upload a file to the specified endpoint
   * 
   * @param {string} endpoint - API endpoint to call
   * @param {File} file - File to upload
   * @param {object} additionalData - Additional data to include in the request
   * @returns {Promise} - Promise that resolves to the response data
   */
  const uploadFile = async (endpoint, file, additionalData = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add any additional data to the FormData
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
      
      const response = await apiClient.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  };
  
  return {
    apiClient,
    submitForm,
    fetchData,
    uploadFile,
    handleApiError
  };
} 